import { create } from 'zustand';
import type { ReceiptData } from '@matix/types';
import {
  appendPrintSection,
  buildReceiptHtml,
  printReceipt,
  printReportInvoice,
  sectionDocument,
  usesReportInvoice,
  POPUP_BLOCKED,
  type PrintSection,
  type PrintReceiptOptions,
} from '@matix/receipt';
import { receiptsAPI } from '@matix/api-client';
import { currentPrintLanguage } from '../features/print/printLanguage';
import { isNativeApp } from '@matix/ui';

/**
 * Single store driving:
 *   1. The auto-print side effect on a successful charge.
 *   2. The "Print again" reprint button.
 *   3. The transient success toast.
 *
 * Why one store: the three concerns share state (the most recent receipt) and
 * the success/dismiss toggling. Splitting them would force a useEffect-bridge
 * between stores or duplicate the receipt blob.
 *
 * The store imports the print utility directly so callers don't need to wire
 * the dependency. That's a tradeoff: it makes the module impure (importing
 * the store triggers nothing, but calling `pushAndPrint` has DOM side
 * effects). The alternative — passing a `print` function in — adds friction
 * for what is a single, stable real-world dependency.
 */
interface ReceiptState {
  /** The most recent receipt — reused by reprint and the success toast. */
  lastReceipt: ReceiptData | null;
  /**
   * A second sheet printed on the same job as the receipt (today: the
   * salesman summary). Kept alongside the receipt so "Print again" — which is
   * also the recovery path when the pop-up blocker ate the first print —
   * reproduces the whole paper, not just the customer's half.
   */
  lastTrailer: PrintSection | null;
  /** Toast visibility. Auto-flips back to false after the timeout in <RecentReceiptToast/>. */
  showToast: boolean;
  /** True only while a print() call is in flight (used to disable the reprint button). */
  printing: boolean;
  /**
   * True when the last print attempt was blocked by the browser's pop-up
   * blocker (the receipt prints in a new window; auto-print from the async
   * post-charge callback can be blocked). The toast shows a hint to allow
   * pop-ups or tap "Print again" (a direct gesture, which is never blocked).
   */
  printBlocked: boolean;
  /**
   * Why the last print failed, in words a cashier can act on — or null when it
   * did not. Only the A4 path can really fail: it fetches the invoice from the
   * report server, and a sale whose invoice never printed must not sit behind a
   * toast that says "printed".
   */
  printError: string | null;

  /**
   * Push a freshly charged receipt and immediately print it, with `trailer`
   * (if any) appended to the same document on its own page.
   *
   * Call this from the POST /orders success path. Idempotency is the caller's
   * responsibility — pushing the same receipt twice will print twice.
   */
  pushAndPrint: (data: ReceiptData, trailer?: PrintSection | null) => Promise<void>;

  /**
   * Reprint the last receipt with a "REPRINT" stripe at the top. Safe to call
   * any time; no-ops if there is no last receipt.
   */
  printAgain: () => Promise<void>;

  /** Hide the success toast (auto-called by the toast component on its timer). */
  dismissToast: () => void;

  /** Reset state on logout. */
  reset: () => void;
}

/** True when an error thrown by printReceipt was the pop-up-blocker signal. */
function isPopupBlocked(err: unknown): boolean {
  return err instanceof Error && err.message === POPUP_BLOCKED;
}

/**
 * Turn a failed A4 fetch into something a cashier can act on. The server never
 * returns the report server's own message (it can name the host and the service
 * account), so the status code is all we have to go on — and it is enough to
 * tell "nobody configured this" apart from "the report server is down".
 */
function printErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 503) return 'A4 invoices are not set up on this server — call support';
  if (status === 404) return 'The report server has no invoice for this receipt';
  if (status !== undefined) return 'The report server could not produce the invoice';
  return 'Could not reach the report server';
}

/**
 * Print the tenant's A4 invoice, which is an SSRS report rather than anything
 * this app lays out — pos-api renders it and we open the PDF in a tab, the way
 * the legacy POS did. `orderId` is the `Inv_Receipt_h.ID` the report keys on.
 *
 * Copies are not looped here: the report opens one tab, and the browser's own
 * print dialog carries a copy count. Looping would just pile up tabs.
 */
async function printReportA4(data: ReceiptData): Promise<void> {
  await printReportInvoice(() =>
    receiptsAPI.getInvoiceReportPdf(data.orderId, currentPrintLanguage()),
  );
}

/**
 * The A4 report invoice is a PDF the report server produced, so nothing can be
 * appended to it — the trailer prints as its own thermal document there.
 */
async function printTrailerAlone(trailer: PrintSection): Promise<void> {
  const lang = currentPrintLanguage();
  await printReceipt(sectionDocument(trailer, { title: 'Salesman Summary', lang }));
}

export const useReceiptStore = create<ReceiptState>((set, get) => ({
  lastReceipt: null,
  lastTrailer: null,
  showToast: false,
  printing: false,
  printBlocked: false,
  printError: null,

  pushAndPrint: async (data, trailer = null) => {
    set({
      lastReceipt: data, lastTrailer: trailer, showToast: true, printing: true,
      printBlocked: false, printError: null,
    });
    try {
      // ⚠ INSIDE THE PHONE SHELL, WHEN A THERMAL SLIP IS ALREADY COMING OUT.
      //
      // `dispatchPrinting` sends the customer slip to SCPS on every order, and
      // SCPS prints it on the branch's thermal printer — none of which involves
      // the phone. The browser path below opens a window and calls print(), and
      // a webview answers that with a "save this PDF" share sheet. Running both
      // ends a sale in two copies and a dialog nobody asked for.
      //
      // So in the app, and ONLY when the server says this branch really has a
      // live thermal route, the automatic print is skipped. The receipt is still
      // stored above, so «طباعة مرة أخرى» produces the PDF copy on demand.
      //
      // ⚠ The condition is the ROUTE, never «are we in the app». Without a route
      // SCPS prints nothing at all, and skipping here would leave the cashier
      // with no copy to hand over — a silent loss, which is worse than a dialog.
      if (isNativeApp() && data.printConfig.thermalCustomerRoute) {
        return;
      }
      // ⚠ AND INSIDE THE PHONE SHELL WITH NO THERMAL ROUTE — THE OTHER HALF.
      //
      // The branch above handles "SCPS is printing, so we must not". This is
      // the opposite case, and until now it fell through to the browser path
      // and ended in a window that neither printed nor closed: `win.print()`
      // is synchronous for the renderer, a webview has no print pipeline for
      // it to drive, and `closesOnAfterprint()` excludes Android on purpose.
      // The cashier was left with a frozen till behind a dead slip.
      //
      // The receipt is still OPENED and still complete — withholding it would
      // be the silent loss the branch above warns about — but nothing fires by
      // itself. The toolbar's Print button is a direct gesture, which is the
      // one form of print that has been reliable on these tablets.
      const nativeNoRoute = isNativeApp() && !data.printConfig.thermalCustomerRoute;
      const shellPrint: PrintReceiptOptions = nativeNoRoute
        ? { autoPrint: false, hint: 'اضغط «طباعة» للطباعة من النظام · Tap Print to print' }
        : {};
      if (usesReportInvoice(data.printConfig)) {
        await printReportA4(data);
        if (trailer) await printTrailerAlone(trailer);
        return;
      }
      // NumberOfReceiptPrint: print the configured number of copies, one after
      // another (each opens its own print window). Clamped to [1, 9].
      const copies = Math.max(1, Math.min(9, data.printConfig.printOptions?.copies ?? 1));
      // The receipt prints in the language the cashier is serving in — the
      // 72 mm ticket as well as the A4 sheet. It is last in the object on
      // purpose: the till knows the language, the server payload does not.
      const receipt = buildReceiptHtml({ ...data, language: currentPrintLanguage() });
      // One window, one print dialog: the trailer rides on the receipt's job
      // behind a page break rather than opening a second tab of its own.
      const html = trailer ? appendPrintSection(receipt, trailer) : receipt;
      for (let i = 0; i < copies; i += 1) {
        await printReceipt(html, shellPrint);
      }
    } catch (err) {
      // Print is best-effort — never let it break the charge flow. Surface a
      // pop-up-blocked hint so the cashier knows to allow pop-ups / reprint,
      // and anything else as a plain message so a missing invoice is visible.
      if (isPopupBlocked(err)) set({ printBlocked: true });
      else set({ printError: printErrorMessage(err) });
    } finally {
      set({ printing: false });
    }
  },

  printAgain: async () => {
    const last = get().lastReceipt;
    const trailer = get().lastTrailer;
    if (!last) return;
    set({ printing: true, printBlocked: false, printError: null });
    try {
      if (usesReportInvoice(last.printConfig)) {
        // The report has no REPRINT stripe to add — SSRS owns the layout — so a
        // reprint is simply the same document again.
        await printReportA4(last);
        if (trailer) await printTrailerAlone(trailer);
        return;
      }
      const receipt = buildReceiptHtml({ ...last, language: currentPrintLanguage(), isReprint: true });
      await printReceipt(trailer ? appendPrintSection(receipt, trailer) : receipt);
    } catch (err) {
      if (isPopupBlocked(err)) set({ printBlocked: true });
      else set({ printError: printErrorMessage(err) });
    } finally {
      set({ printing: false });
    }
  },

  dismissToast: () => set({ showToast: false }),

  reset: () => set({
    lastReceipt: null, lastTrailer: null, showToast: false, printing: false,
    printBlocked: false, printError: null,
  }),
}));
